import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  TextInput,
  Select,
  Button,
  Group,
  Stack,
  Grid,
  Text,
} from "@mantine/core";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { USER_TYPES, formatPhone, uniqueOptions } from "./AddRepFromLeadModal";

const userTypeOf = role => {
  if (role === "COMPANY_ADMIN") return "company_admin";
  if (role === "MANAGER") return "manager";
  return "rep";
};

// The form's values, seeded from GET /v2/rep/:user_id
const seedFrom = rep => ({
  firstName: rep.first_name || "",
  lastName: rep.last_name || "",
  email: rep.email || "",
  phone: formatPhone(rep.phone_number || ""),
  managerUserId: rep.managers?.[0]?.user_id
    ? String(rep.managers[0].user_id)
    : null,
  recruitedByUserId: rep.recruited_by?.user_id
    ? String(rep.recruited_by.user_id)
    : null,
  repTypeId: rep.rep_type?.id ? String(rep.rep_type.id) : null,
  recruitingSourceId: rep.recruiting_source?.id
    ? String(rep.recruiting_source.id)
    : null,
  userType: userTypeOf(rep.role),
});

/// The profile's contact editor, the web version of iOS's Edit Rep form.
/// Sends only what changed with PUT /v2/rep/:user_id. `rep` is the
/// GET /v2/rep/:user_id payload.
function EditRepModal({ opened, rep, onClose, onSaved }) {
  const { getVerifiedJWT, user } = useAuth();
  const { showSuccess } = useToast();

  const [original, setOriginal] = useState(null);
  const [form, setForm] = useState(null);
  const [people, setPeople] = useState([]);
  const [repTypes, setRepTypes] = useState([]);
  const [recruitingSources, setRecruitingSources] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  useEffect(() => {
    if (!opened || !rep) return undefined;
    const seeded = seedFrom(rep);
    setOriginal(seeded);
    setForm(seeded);
    setError(null);

    let cancelled = false;
    const load = async () => {
      try {
        const jwt = await getVerifiedJWT();
        if (!jwt) return;
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        };
        const [peopleRes, typesRes, sourcesRes] = await Promise.all([
          fetch(
            getApiEndpoint(`/v2/company-downline/downline/rep/${user.repId}`),
            { headers }
          ),
          fetch(getApiEndpoint("/v2/company/rep-types"), { headers }),
          fetch(getApiEndpoint("/v2/company/recruiting-sources"), { headers }),
        ]);
        if (peopleRes.ok) {
          const users = ((await peopleRes.json()).data || []).filter(
            u => u.rep_id != null
          );
          if (!cancelled) setPeople(users);
        }
        if (typesRes.ok) {
          const types = (await typesRes.json()).data || [];
          if (!cancelled) setRepTypes(types);
        }
        if (sourcesRes.ok) {
          const sources = (await sourcesRes.json()).data || [];
          if (!cancelled) setRecruitingSources(sources);
        }
      } catch {
        // Dropdowns are best-effort
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, rep]);

  const personName = p => `${p.first_name || ""} ${p.last_name || ""}`.trim();

  // The current manager and recruiter are usually above this rep, outside
  // the viewer's downline list, so add them or the pickers would show blank
  const managerOptions = useMemo(() => {
    const current = (rep?.managers || []).map(m => ({
      value: String(m.user_id),
      label: personName(m),
    }));
    const fromDownline = people
      .filter(p =>
        ["MANAGER", "COMPANY_ADMIN"].includes((p.role_code || "").toUpperCase())
      )
      .map(p => ({ value: String(p.id), label: personName(p) }));
    return uniqueOptions([...current, ...fromDownline]);
  }, [people, rep]);

  const recruiterOptions = useMemo(() => {
    const current = rep?.recruited_by
      ? [
          {
            value: String(rep.recruited_by.user_id),
            label: personName(rep.recruited_by),
          },
        ]
      : [];
    return uniqueOptions([
      ...current,
      ...people.map(p => ({ value: String(p.id), label: personName(p) })),
    ]);
  }, [people, rep]);

  const isCompanyAdmin = user?.role === "COMPANY_ADMIN";
  const userTypeOptions = USER_TYPES.filter(
    type => type.value !== "company_admin" || isCompanyAdmin
  );

  if (!form || !original) return null;

  const changed = field => form[field] !== original[field];
  const anyChanged = Object.keys(form).some(changed);
  const canSave =
    anyChanged &&
    form.firstName.trim() &&
    form.lastName.trim() &&
    form.email.trim().length > 3 &&
    form.managerUserId &&
    form.recruitedByUserId &&
    form.repTypeId &&
    form.userType &&
    !saving;

  const handleSave = async () => {
    if (!canSave) return;
    const body = {};
    if (changed("firstName")) body.first_name = form.firstName.trim();
    if (changed("lastName")) body.last_name = form.lastName.trim();
    if (changed("email")) body.email = form.email.trim();
    // An empty string clears it
    if (changed("phone")) body.phone_number = form.phone.trim();
    if (changed("managerUserId")) {
      body.manager_user_id = Number(form.managerUserId);
    }
    if (changed("recruitedByUserId")) {
      body.recruited_by_user_id = Number(form.recruitedByUserId);
    }
    if (changed("repTypeId")) body.rep_type_id = Number(form.repTypeId);
    if (changed("recruitingSourceId")) {
      body.recruiting_source_id = form.recruitingSourceId
        ? Number(form.recruitingSourceId)
        : null;
    }
    if (changed("userType")) {
      body.is_manager = form.userType === "manager";
      if (isCompanyAdmin) {
        body.is_company_admin = form.userType === "company_admin";
      }
    }

    setSaving(true);
    setError(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      const response = await fetch(getApiEndpoint(`/v2/rep/${rep.user_id}`), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        let message = "Couldn't save changes";
        try {
          const data = await response.json();
          const raw = data.message ?? data.error;
          if (raw) message = Array.isArray(raw) ? raw.join(", ") : raw;
        } catch {
          // keep the default
        }
        throw new Error(message);
      }
      showSuccess("Rep updated");
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message || "Couldn't save changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Edit Rep"
      centered
      size="lg"
    >
      <Stack gap="md">
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="First name"
              placeholder="First name"
              value={form.firstName}
              onChange={e =>
                set("firstName", e.currentTarget.value.replace(/\d/g, ""))
              }
              required
              disabled={saving}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Last name"
              placeholder="Last name"
              value={form.lastName}
              onChange={e =>
                set("lastName", e.currentTarget.value.replace(/\d/g, ""))
              }
              required
              disabled={saving}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              type="email"
              label="Email"
              placeholder="name@email.com"
              value={form.email}
              onChange={e => set("email", e.currentTarget.value)}
              required
              disabled={saving}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              type="tel"
              label="Phone"
              placeholder="(000) 000-0000"
              value={form.phone}
              onChange={e => set("phone", formatPhone(e.currentTarget.value))}
              disabled={saving}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="Manager"
              placeholder="Select manager"
              data={managerOptions}
              value={form.managerUserId}
              onChange={value => set("managerUserId", value)}
              searchable
              required
              disabled={saving}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="User type"
              data={userTypeOptions}
              value={form.userType}
              onChange={value => set("userType", value)}
              allowDeselect={false}
              required
              disabled={saving}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="Recruited by"
              placeholder="Select rep"
              data={recruiterOptions}
              value={form.recruitedByUserId}
              onChange={value => set("recruitedByUserId", value)}
              searchable
              required
              disabled={saving}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Select
              label="Rep type"
              placeholder="Select type"
              data={uniqueOptions(
                repTypes.map(t => ({ value: String(t.id), label: t.name }))
              )}
              value={form.repTypeId}
              onChange={value => set("repTypeId", value)}
              searchable
              required
              disabled={saving}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Select
              label="Recruiting source"
              placeholder="Select source"
              data={uniqueOptions(
                recruitingSources.map(s => ({
                  value: String(s.id),
                  label: s.source_name,
                }))
              )}
              value={form.recruitingSourceId}
              onChange={value => set("recruitingSourceId", value)}
              searchable
              clearable
              disabled={saving}
            />
          </Grid.Col>
        </Grid>

        {error && (
          <Text c="red" size="sm">
            {error}
          </Text>
        )}

        <Group justify="flex-end" mt="xs">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={!canSave}>
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default EditRepModal;
